import { Module, VuexModule, Action, Mutation } from 'vuex-module-decorators';
import { Inject } from 'inversify-props';
import { CustomersService } from '@/services/customers.service';
import { Customer } from '@/models/DTO/customer/customer.model';
import { CustomersActions } from '@/constants/actions.constants';
import { CustomersMutations } from '@/constants/mutation.constants';
import { BaseAPIErrorModel } from '@/models/DTO/base-api-error.model';
import { RegisterCustomerModel } from '@/models/DTO/customer/register-customer.model';
import { CustomerUpdateRequest } from '@/models/DTO/customer/customer-update-request.model';
import { CustomerGetters } from '@/constants/getters.constants';
import { AuthenticationService } from '@/services/authentication.service';

export const CUSTOMERS_MODULE_NAME = 'customers';

class DefaultState {
  public customers: Customer[] = [];
  public currentCustomer: Customer | null = null;
  public publicCustomer: Customer | null = null;
  public errorResponse: BaseAPIErrorModel | null = null;
  public errorLookupResponse: BaseAPIErrorModel | null = null;
  public showLoading = false;
  public showButtonLoading = false;
}

const defaultState = new DefaultState();

@Module({
  namespaced: true,
  name: CUSTOMERS_MODULE_NAME
})
export default class CustomersModule extends VuexModule {
  // #region Services
  @Inject()
  private readonly _customersService!: CustomersService;

  @Inject()
  private readonly _authenticationService!: AuthenticationService;
  // #endregion

  // #region State
  public customers: Customer[] = defaultState.customers;
  public currentCustomer: Customer | null = defaultState.currentCustomer;
  public publicCustomer: Customer | null = defaultState.publicCustomer;
  public showLoading = defaultState.showLoading;
  public showButtonLoading = defaultState.showButtonLoading;
  public errorResponse: BaseAPIErrorModel | null = defaultState.errorResponse;
  public errorLookupResponse: BaseAPIErrorModel | null =
    defaultState.errorLookupResponse;
  // #endregion

  // #region Getters
  public get [CustomerGetters.hasErrorMessages](): boolean {
    return this.context.getters[CustomerGetters.errorMessages].length > 0;
  }

  public get [CustomerGetters.errorMessages](): string[] {
    return this.errorResponse?.getAllErrorMessages?.() ?? [];
  }
  // #endregion

  // #region Actions
  @Action
  public async [CustomersActions.getAllCustomersAction](): Promise<void> {
    try {
      this.context.commit(CustomersMutations.showLoadingMutation, true);

      // remove any errors
      this.context.commit(CustomersMutations.errorMutation, null);

      const resp = await this._customersService.getAllCustomers();

      // success
      this.context.commit(CustomersMutations.setAllCustomersMutation, [
        ...resp
      ]);
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(CustomersMutations.errorMutation, e);
    } finally {
      this.context.commit(CustomersMutations.showLoadingMutation, false);
    }
  }

  @Action
  public async [CustomersActions.getCustomerByIdAction](
    id: string
  ): Promise<void> {
    try {
      this.context.commit(CustomersMutations.showLoadingMutation, true);

      // remove any errors
      this.context.commit(CustomersMutations.errorMutation, null);

      const resp = await this._customersService.getCustomerById(id);

      // success
      this.context.commit(CustomersMutations.setCurrentCustomerMutation, {
        ...resp
      });
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(CustomersMutations.errorMutation, e);
    } finally {
      this.context.commit(CustomersMutations.showLoadingMutation, false);
    }
  }

  @Action
  public async [CustomersActions.getCustomerByEmailAction](
    email: string
  ): Promise<void> {
    try {
      this.context.commit(CustomersMutations.showLoadingMutation, true);

      // remove any errors
      this.context.commit(CustomersMutations.errorLookupMutation, null);

      const resp = await this._customersService.getCustomerByEmail(email);

      // success
      this.context.commit(CustomersMutations.setCurrentCustomerMutation, {
        ...resp
      });
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(CustomersMutations.errorLookupMutation, e);
    } finally {
      this.context.commit(CustomersMutations.showLoadingMutation, false);
    }
  }

  @Action
  public async [CustomersActions.registerCustomerAction](
    payload: RegisterCustomerModel
  ): Promise<void> {
    try {
      this.context.commit(CustomersMutations.showButtonLoadingMutation, true);

      // remove any errors
      this.context.commit(CustomersMutations.errorMutation, null);

      const resp = await this._authenticationService.registerCustomer({
        ...payload
      });

      // success
      this.context.commit(CustomersMutations.setCurrentCustomerMutation, {
        ...resp
      });
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(CustomersMutations.errorMutation, e);
    } finally {
      this.context.commit(CustomersMutations.showButtonLoadingMutation, false);
    }
  }

  @Action
  public async [CustomersActions.registerCustomerIfNeededAction](
    payload: RegisterCustomerModel
  ): Promise<void> {
    try {
      this.context.commit(CustomersMutations.showButtonLoadingMutation, true);

      // remove any errors
      this.context.commit(CustomersMutations.errorMutation, null);

      const resp = await this._authenticationService.registerCustomerIfNeeded({
        ...payload
      });

      // success
      this.context.commit(CustomersMutations.setCurrentCustomerMutation, {
        ...resp
      });
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(CustomersMutations.errorMutation, e);
    } finally {
      this.context.commit(CustomersMutations.showButtonLoadingMutation, false);
    }
  }

  @Action
  public async [CustomersActions.updateCustomerAction](
    payload: CustomerUpdateRequest
  ): Promise<void> {
    try {
      this.context.commit(CustomersMutations.showButtonLoadingMutation, true);

      // remove any errors
      this.context.commit(CustomersMutations.errorMutation, null);

      const resp = await this._customersService.updateCustomer({
        ...payload
      });

      // success
      this.context.commit(CustomersMutations.setCurrentCustomerMutation, {
        ...resp
      });
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(CustomersMutations.errorMutation, e);
    } finally {
      this.context.commit(CustomersMutations.showButtonLoadingMutation, false);
    }
  }

  /**
   * Stores customer data until Paya payment is confirmed
   * @param payload
   */
  @Action
  public async [CustomersActions.setPublicCustomerAction](
    payload: RegisterCustomerModel
  ): Promise<void> {
    return this.context.commit(CustomersMutations.setPublicCustomerMutation, {
      ...payload
    });
  }

  @Action
  public async [CustomersActions.clearCurrentCustomerAction](): Promise<void> {
    return this.context.commit(CustomersMutations.clearCurrentCustomerMutation);
  }

  @Action
  public async [CustomersActions.resetStateAction](): Promise<void> {
    return this.context.commit(CustomersMutations.resetStateMutation);
  }
  // #endregion

  // #region Mutations
  @Mutation
  private [CustomersMutations.setAllCustomersMutation](data: Customer[]) {
    this.customers = [...data];
  }

  @Mutation
  private [CustomersMutations.setCurrentCustomerMutation](data: Customer) {
    this.currentCustomer = { ...data };
  }

  @Mutation
  private [CustomersMutations.showLoadingMutation](show: boolean) {
    this.showLoading = show;
  }

  @Mutation
  private [CustomersMutations.showButtonLoadingMutation](show: boolean) {
    this.showButtonLoading = show;
  }

  @Mutation
  private [CustomersMutations.errorMutation](
    errorModel: BaseAPIErrorModel | null
  ) {
    this.errorResponse = errorModel;
  }

  @Mutation
  private [CustomersMutations.errorLookupMutation](
    errorModel: BaseAPIErrorModel | null
  ) {
    this.errorLookupResponse = errorModel;
  }

  @Mutation
  private [CustomersMutations.setPublicCustomerMutation](data: Customer) {
    this.publicCustomer = { ...data };
  }

  @Mutation
  private [CustomersMutations.clearCurrentCustomerMutation]() {
    this.currentCustomer = null;
  }

  @Mutation
  private [CustomersMutations.resetStateMutation]() {
    Object.assign(this, defaultState);
  }
  // #endregion
}
